"use client";

import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

export type CourtRow = {
  id: string;
  name: string;
  description: string | null;
  sport: string | null;
  surface: string;
  capacity: number;
  pricePerSlot: number | null;
  durationMinutes: number;
  color: string | null;
  isActive: boolean;
};

const SURFACES = [
  { value: "SYNTHETIC", label: "Sintético" },
  { value: "CLAY", label: "Tierra batida" },
  { value: "GRASS", label: "Césped natural" },
  { value: "HARD", label: "Dura" },
  { value: "WOOD", label: "Madera" },
  { value: "OTHER", label: "Otra" },
];

const label = css({ fontSize: "xs", color: "#64748b" });

/** Modal único para crear y editar canchas (mismo componente, distinto modo según `court`). */
export function CourtFormModal({
  open,
  onClose,
  court,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** null = modo creación; con datos = modo edición, precargado. */
  court: CourtRow | null;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const isEdit = Boolean(court);
  const [name, setName] = useState("");
  const [sport, setSport] = useState("");
  const [surface, setSurface] = useState("SYNTHETIC");
  const [capacity, setCapacity] = useState("4");
  const [durationMinutes, setDurationMinutes] = useState("60");
  const [pricePerSlot, setPricePerSlot] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setName(court?.name ?? "");
    setSport(court?.sport ?? "");
    setSurface(court?.surface ?? "SYNTHETIC");
    setCapacity(String(court?.capacity ?? 4));
    setDurationMinutes(String(court?.durationMinutes ?? 60));
    setPricePerSlot(court?.pricePerSlot != null ? String(court.pricePerSlot) : "");
    setDescription(court?.description ?? "");
  }, [open, court]);

  async function submit() {
    setErr(null);
    const nameTrim = name.trim();
    if (!nameTrim) {
      setErr("El nombre es requerido.");
      return;
    }
    const capacityNum = Number(capacity) || 2;
    const durationNum = Number(durationMinutes) || 60;
    const priceRaw = pricePerSlot.trim();
    const priceNum = priceRaw === "" ? null : Number(priceRaw);

    const body = {
      name: nameTrim,
      sport: sport.trim() || null,
      surface,
      capacity: capacityNum,
      durationMinutes: durationNum,
      pricePerSlot: priceNum,
      description: description.trim() || null,
    };

    setBusy(true);
    try {
      const r = isEdit
        ? await fetchWithSessionSync(
            `/api/dashboard/courts/${court!.id}`,
            { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
            refresh,
          )
        : await fetchWithSessionSync(
            "/api/dashboard/courts",
            { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
            refresh,
          );
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setErr(j.error ?? (isEdit ? "No se pudo guardar la cancha." : "No se pudo crear la cancha."));
        return;
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Editar cancha" : "Nueva cancha"} size="md">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}

        <div className={css({ display: "grid", gap: "3", gridTemplateColumns: "1fr 1fr" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Nombre *</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cancha 1" />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Deporte</span>
            <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="Pádel" />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Superficie</span>
            <select value={surface} onChange={(e) => setSurface(e.target.value)} className={css({
              width: "100%", px: "3", py: "2", rounded: "md", borderWidth: "1px",
              borderColor: "border.DEFAULT", bg: "bg.DEFAULT", color: "fg.DEFAULT", fontSize: "sm",
            })}>
              {SURFACES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Capacidad (jugadores máx.)</span>
            <Input type="number" min={1} max={100} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Duración del turno (min)</span>
            <Input type="number" min={15} step={15} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={label}>Precio por turno (opcional)</span>
            <Input
              type="number"
              min={0}
              step={1}
              value={pricePerSlot}
              onChange={(e) => setPricePerSlot(e.target.value)}
              placeholder="Dejar vacío si no aplica"
            />
          </label>
        </div>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={label}>Descripción (opcional)</span>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end", pt: "2" })}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear cancha"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
